<template>
  <div class="vz-individual-order-cargo pdf-order-section" :class="{'vz-individual-order-cargo-disabled': disabled}">
    <div class="vz-individual-order-cargo-title text-h1 color-light mb-20">
      <span>{{ title }}</span>
      <vz-card v-if="orderStore.orderCargoConditionHint" variant="warning" class="px-10 py-5 active fs-12" style="display: inline">
        {{ orderStore.orderCargoConditionHint }}
      </vz-card>
    </div>
    <div class="vz-personal-order-blocks tiny-gap">
      <div class="vz-position-relative">
        <div v-if="disabled" class="disabled-card"/>
        <div class="vz-individual-order-cargo-type flex">
          <vz-checkbox
            v-model="isTypeCargo"
            :disabled="disabled"
            label="Груз"
          />
          <vz-checkbox
            v-model="isTypeCorrespondence"
            :disabled="disabled"
            label="Корреспонденция"
          />
          <vz-tooltip-info class="vz-individual-order-cargo-tooltip" flex>
            <template #baloon>
              <span>{{ tooltip }}</span>
            </template>
          </vz-tooltip-info>
        </div>
        <div class="vz-individual-order-cargo-overlay-wrapper">
          <vz-switcher
            v-model="mode"
            :items="cargoTabs"
            big
            :disabled="(isOnlyCorrespondence || orderLock) && !isCloned"
            class="text-caption mt-10 vz-individual-order-cargo-switcher"
          />
          <keep-alive>
            <div class="flex flex-wrap">
              <component :is="cargoModesComponent" :disabled="orderLock" @set-mode="mode = $event"/>
            </div>
          </keep-alive>
          <div v-if="isOnlyCorrespondence" class="vz-individual-order-cargo-overlay"/>
        </div>
      </div>

      <div class="vz-position-relative">
        <div v-if="disabled" class="disabled-card"/>

        <vz-cargo-category
          v-if="!simple"
          :disabled="disabled"
          class="mb-20"
        />

        <vz-cargo-wrapping
          v-if="!isParcelsMode"
          :disabled="disabled"
          :simple="simple"
        />

        <vz-cargo-insurance-transportation
          :disabled="disabled"
          :hide-custom-id="simple"
        />

        <vz-checkbox-collapse
          v-if="!simple"
          class="mt-20"
          v-model="isNeedMarketplace"
          :disabled="disabled"
          collapsible
          label="Заявка с маркетплейса"
        >
          <vz-autocomplete
            :modelValue="marketplaceGuid"
            placeholder="Выберите маркетплейс"
            value-template="{name}"
            title-template="{name}"
            description-template="{description}"
            value-key="guid"
            :items="marketplaceList"
            :disabled="disabled"
            big
          />
        </vz-checkbox-collapse>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {SwitchItem} from '~/types/types';
import VzCargoWrapping from '~/components/personal/order/cargo/vz-cargo-wrapping.vue';
import VzCargoInsuranceTransportation
  from '~/components/personal/order/cargo/parts/vz-cargo-insurance-transportation.vue';
import useCargoBlock from '~/components/personal/order/cargo/useCargoBlock';
import VzCargoCategory from '~/components/personal/order/cargo/vz-cargo-category.vue';
import VzCheckboxCollapse from "~/components/ui-kit/vz-checkbox-collapse.vue";
import {useOrderMarketplace} from "~/stores/personal/order-marketplace";
import {toRefs} from "@vue/reactivity";
import {useOrderStore} from "~/stores/order";
import VzAutocomplete from "~/components/ui-kit/vz-autocomplete.vue";
import VzCard from '~/components/ui-kit/vz-card.vue';
import VzCheckbox from '~/components/ui-kit/vz-checkbox.vue';
import VzTooltipInfo from '~/components/ui-kit/vz-tooltip-info.vue';
import VzSwitcher from '~/components/ui-kit/vz-switcher.vue';

const props = defineProps({
  title: {
    type: String,
    default: 'Груз'
  },
  simple: {
    type: Boolean,
    default: false
  }
})

const orderStore = useOrderStore()
const {receiveMarketplaces} = useOrderMarketplace()
const {marketplaceList} = toRefs(useOrderMarketplace())
const {
  tooltip, isTypeCorrespondence, isTypeCargo,
  mode, disabled, cargoModesComponent, isParcelsMode,
  orderLock, isOnlyCorrespondence, isCloned
} = useCargoBlock()

// ssr
await receiveMarketplaces()

const isNeedMarketplace = ref(!!orderStore.orderForm.cargo.marketplaceGuid);

const cargoTabs = computed((): SwitchItem[] => {
  let result = [
    {
      title: 'Габариты',
      value: 'dimension'
    },
    {
      title: 'Места',
      value: 'places'
    },
    {
      title: 'Посылка',
      value: 'parcels'
    },
    {
      title: 'Паллета',
      value: 'pallets',
      disabled: orderStore.orderConditionValues.cargo?.dimension?.pallets?.disabled,
      disabledText: orderStore.orderConditionValues.cargo?.dimension?.pallets?.reason
    }
  ]

  if (props.simple) {
    result = result.filter(item => item.value === 'dimension' || item.value === 'places')
  }

  return result
})

const marketplaceGuid = computed({
  get() {
    return orderStore.orderForm.cargo.marketplaceGuid
  },
  set(value?: string|null) {
    orderStore.orderForm.cargo.marketplaceGuid = value;
    orderStore.getOrderPriceAction()
  }
})

watch(() => isNeedMarketplace.value, (res) => {
  if (res && marketplaceList.value.length && !orderStore.orderForm.cargo.marketplaceGuid) {
    orderStore.orderForm.cargo.marketplaceGuid = marketplaceList.value[0].guid;
  } else {
    orderStore.orderForm.cargo.marketplaceGuid = null;
  }

  orderStore.getOrderPriceAction()
})
</script>

<style lang="scss">
.vz-individual-order-cargo {
  .vz-individual-order-cargo-switcher {
    .vz-switcher-item {
      padding: 10px 5px !important;

      @media screen and (max-width: 400px) {
        padding: 10px 20px !important;
      }
    }
  }

  &-type {
    @media screen and (max-width: 400px) {
      flex-wrap: wrap;
    }

    .vz-checkbox {
      @media screen and (max-width: 400px) {
        margin-bottom: 10px;
      }   
    
      &:first-child {
        margin-right: 20px;

        @media screen and (max-width: 1280px) {
          margin-right: 10px;
        }
      }
    }
  }
}
</style>
